import { filter, mapObjIndexed } from 'ramda';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import * as actions from '../actions';
import { $getCompanies, getLightCompanyById } from './companies';

function byId(state = {}, action) {
  switch (action.type) {
    case actions.REQUEST_USERLIST_SUCCESS:
    case actions.REVIEWING_ASK_SUCCESS:
    case actions.REQUEST_QUESTIONNAIRELIST_SUCCESS:
    case actions.REQUEST_REVIEWING_QUESTIONNAIRES_SUCCESS:
      return Object.assign(state, action.data.items.entities.users || {});
    case actions.QUESTIONNAIRE_ASSIGNUSER_SUCCESS:
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
      return Object.assign(state, action.data.entities.users || {});
    case actions.REQUEST_COMPANY_BUDGETS_SUCCESS: {
      const mergedUsers = mapObjIndexed((u, id) => {
        const user = state[id] || {};
        return Object.assign({}, user, u);
      });

      return Object.assign({}, state, mergedUsers);
    }
    case actions.REQUEST_ORDER_SUCCESS:
    case actions.PLACE_ORDER_NATIONAL_SUCCESS:
    case actions.REQUEST_QUESTIONNAIRE_SUCCESS:
    case actions.UPDATE_QUESTIONNAIRE_SUCCESS:
    case actions.SEND_QUESTIONNAIRE_SUCCESS:
      return Object.assign({}, state, action.data.response.entities.users);
    case actions.RESEND_ACCOUNT_ACTIVATION_EMAIL_SUCCESS:
      return Object.assign({}, state, action.data.entities.users || {});
    case actions.REQUEST_USERLIST_START:
    case actions.REQUEST_USERLIST_ERROR:
      return {};
    default:
      return Object.assign({}, state);
  }
}

function ids(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_USERLIST_SUCCESS:
      return action.data.items.result;
    case actions.REQUEST_USERLIST_START:
    case actions.REQUEST_CLIENT_USERLIST_START:
    case actions.REQUEST_NON_CLIENT_USERLIST_START:
    case actions.REQUEST_EDITOR_USERLIST_START:
    case actions.REQUEST_USERLIST_ERROR:
      return [];
    default:
      return Object.assign([], state);
  }
}

function loading(state = false, action) {
  switch (action.type) {
    case actions.REQUEST_USERLIST_START:
    case actions.REQUEST_CLIENT_USERLIST_START:
    case actions.REQUEST_NON_CLIENT_USERLIST_START:
    case actions.REQUEST_EDITOR_USERLIST_START:
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REFUSE_USER:
    case actions.ACCEPT_USER:
    case actions.RESEND_ACCOUNT_ACTIVATION_EMAIL_START:
    case actions.EDIT_USER_START:
    case actions.REMOVE_USER_START:
    case actions.ADD_USER:
      return true;
    case actions.REQUEST_USERLIST_SUCCESS:
    case actions.REQUEST_USERLIST_ERROR:
    case actions.ACCEPT_USER_SUCCESS:
    case actions.ACCEPT_USER_ERROR:
    case actions.REFUSE_USER_SUCCESS:
    case actions.REFUSE_USER_ERROR:
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
    case actions.RESEND_ACCOUNT_ACTIVATION_EMAIL_SUCCESS:
    case actions.RESEND_ACCOUNT_ACTIVATION_EMAIL_ERROR:
    case actions.EDIT_USER_SUCCESS:
    case actions.EDIT_USER_ERROR:
    case actions.REMOVE_USER_SUCCESS:
    case actions.REMOVE_USER_ERROR:
    case actions.ADD_USER_SUCCESS:
    case actions.ADD_USER_ERROR:
      return false;
    default:
      return state;
  }
}

function error(state = null, action) {
  switch (action.type) {
    case actions.CONFIRMED_USER_ERROR:
    case actions.REQUEST_USERLIST_ERROR:
    case actions.ACCEPT_USER_ERROR:
    case actions.REFUSE_USER_ERROR:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
    case actions.EDIT_USER_ERROR:
    case actions.REMOVE_USER_ERROR:
    case actions.ADD_USER_ERROR:
      return action.err.message || process.env.ERR_GENERIC;
    case actions.REQUEST_USERLIST_START:
    case actions.REFUSE_USER:
    case actions.ACCEPT_USER:
    case actions.REQUEST_USERLIST_SUCCESS:
    case actions.REVIEWING_ASK_SUCCESS:
    case actions.REFUSE_USER_SUCCESS:
    case actions.ACCEPT_USER_SUCCESS:
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
    case actions.EDIT_USER_START:
    case actions.EDIT_USER_SUCCESS:
    case actions.REMOVE_USER_START:
    case actions.REMOVE_USER_SUCCESS:
    case actions.ADD_USER:
    case actions.ADD_USER_SUCCESS:
      return null;
    default:
      return state;
  }
}

function reviewingIds(state = [], action) {
  switch (action.type) {
    case actions.REVIEWING_ASK:
    case actions.REVIEWING_ASK_ERROR:
      return [];
    case actions.REVIEWING_ASK_SUCCESS:
      return action.data.items.result;
    case actions.REFUSE_USER_SUCCESS:
    case actions.ACCEPT_USER_SUCCESS:
      return filter(id => id !== action.user.id, state);
    default:
      return Object.assign([], state);
  }
}

function masterIds(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
      return [];
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
      return action.data.masters;
    default:
      return Object.assign([], state);
  }
}

function directorIds(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
      return [];
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
      return action.data.directors;
    default:
      return Object.assign([], state);
  }
}

function buyerIds(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
      return [];
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
      return action.data.buyers;
    default:
      return Object.assign([], state);
  }
}

function baseIds(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_STRUCTURE_START:
    case actions.REQUEST_COMPANY_STRUCTURE_ERROR:
      return [];
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS:
      return action.data.bases;
    default:
      return Object.assign([], state);
  }
}

function total(state = 0, action) {
  switch (action.type) {
    case actions.REQUEST_USERLIST_SUCCESS:
      return action.data.total;
    case actions.REQUEST_USERLIST_START:
    case actions.REQUEST_USERLIST_ERROR:
      return 0;
    default:
      return state;
  }
}

const users = combineReducers({
  byId,
  ids,
  loading,
  error,
  reviewingIds,
  masterIds,
  directorIds,
  buyerIds,
  baseIds,
  total,
});

export default users;

const $getIds = state => state.users.ids;
const $getUsers = state => state.users.byId;
const $getRewiewingIds = state => state.users.reviewingIds || [];
const $getTotalUsers = state => state.users.total;
const $getLoading = state => state.users.loading;
const $getBaseIds = state => state.users.baseIds;
const $getMasterIds = state => state.users.masterIds;
const $getDirectorIds = state => state.users.directorIds;
const $getBuyerIds = state => state.users.buyerIds;

const $mapUserType =
  ($ids = [], $users = []) =>
    $ids.reduce((obj, id) => Object.assign(obj, { [id]: $users[id] }), {});

export const $getUserById = (state, id) => state.users.byId[id];

export const getUserById = (state, id) => {
  const $user = $getUserById(state, id);
  return $user && Object.assign({}, $user, {
    company: getLightCompanyById(state, $user.company),
  });
};

export const getUsers = createSelector(
  $getIds, $getUsers, $getCompanies,
  ($ids, $users, $companies) =>
    $ids.map(id => Object.assign(
      {},
      $users[id],
      { company: $companies[$users[id].company] })));

export const getReviewingUsersTotal = createSelector(
  $getRewiewingIds,
  $ids => $ids.length);

export const getReviewingUsers = createSelector(
  $getRewiewingIds, $getUsers, $getCompanies,
  ($ids, $users, $companies) =>
    $ids.map(id => Object.assign(
      {},
      $users[id],
      { company: $companies[$users[id].company] })));

export const getTotalUsers = createSelector(
  $getTotalUsers,
  $total => $total);

export const getMasterUsers = createSelector(
  $getMasterIds,
  $getUsers,
  $mapUserType,
);

export const getBaseUsers = createSelector(
  $getBaseIds,
  $getUsers,
  $mapUserType,
);

export const getDirectorUsers = createSelector(
  $getDirectorIds,
  $getUsers,
  $mapUserType,
);

export const getBuyerUsers = createSelector(
  $getBuyerIds,
  $getUsers,
  $mapUserType,
);
export const isLoading = createSelector(
  $getLoading,
  $loading => $loading);
